import React, { useState, useRef, useEffect } from "react";
import {
  X,
  Send,
  Sparkles,
  RefreshCw,
  Check,
  GripVertical,
  RotateCcw,
  Paperclip,
  Image as ImageIcon,
  Trash2,
} from "lucide-react";
import {
  GEMINI_FLASH_MODEL,
  normalizeGeminiGenerationModel,
} from "../utils/geminiModel";

/**
 * AI Chat Panel - 그리드 데이터를 자연어로 편집하는 AI 채팅 패널
 * Google Sheets Gemini / Cursor 스타일
 * 리사이즈 가능 (왼쪽 드래그)
 */
const AIChatPanel = ({
  isOpen,
  onClose,
  selectedColumn,
  selectedData,
  onApplyTransform,
  apiBaseUrl = "http://localhost:3002",
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [pendingTransform, setPendingTransform] = useState(null);
  const [panelWidth, setPanelWidth] = useState(380);
  const [isResizing, setIsResizing] = useState(false);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  // 🔄 Undo 스택 - 변경 이력 관리 (Phase 1: 로컬 메모리)
  const [undoStack, setUndoStack] = useState([]);

  // 🔀 열 분리 대기 상태
  const [pendingSplit, setPendingSplit] = useState(null);

  // 🖼️ 이미지 첨부 상태
  const [attachedImages, setAttachedImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const MIN_WIDTH = 320;
  const MAX_WIDTH = 700;

  // 리사이즈 핸들러
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const newWidth = window.innerWidth - e.clientX;
      setPanelWidth(Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";

      // 🔧 AI 패널 리사이즈 후 AUIGrid resize
      setTimeout(() => {
        if (window.AUIGrid && window.AUIGrid.isCreated("#horizontal_grid")) {
          window.AUIGrid.resize("#horizontal_grid");
          console.log("📐 [AI Panel Resize] AUIGrid resize");
        }
      }, 100);
    };

    if (isResizing) {
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  // 자동 스크롤
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // 패널 열릴 때 입력창 포커스 + 그리드 컬럼 가져오기
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);

      // 🔧 AUIGrid에서 컬럼 목록 가져오기
      if (window.AUIGrid && window.AUIGrid.isCreated("#horizontal_grid")) {
        try {
          const columnLayout =
            window.AUIGrid.getColumnLayout("#horizontal_grid");
          const cols = columnLayout
            .filter(
              (c) =>
                c.dataField &&
                c.dataField !== "__checkbox" &&
                c.dataField !== "__rownum",
            )
            .map((c) => ({
              field: c.dataField,
              label: c.headerText || c.dataField,
            }));
          setAvailableColumns(cols);
          // 기본적으로 모든 컬럼 선택
          setSelectedColumns(cols.map((c) => c.field));
          console.log("📊 [AI] Available columns:", cols.length);
        } catch (err) {
          console.error("컬럼 정보 가져오기 실패:", err);
        }
      }
    }
  }, [isOpen]);

  // 선택된 데이터 컨텍스트 메시지 추가
  useEffect(() => {
    if (selectedColumn && selectedData?.length > 0) {
      const contextMsg = {
        role: "system",
        content: `📊 **${selectedColumn}** 컬럼 선택됨 (${selectedData.length}개 셀)`,
        preview:
          selectedData
            .slice(0, 5)
            .map((d) => d.value || d)
            .join(", ") + (selectedData.length > 5 ? "..." : ""),
      };
      setMessages((prev) => {
        const filtered = prev.filter(
          (m) => m.role !== "system" || !m.content.includes("컬럼 선택됨"),
        );
        return [...filtered, contextMsg];
      });
    }
  }, [selectedColumn, selectedData]);

  // 🖼️ 이미지 헬퍼: 파일 → {id, file, dataUrl, name} 변환 후 상태에 추가
  const addImages = (files) => {
    const imageFiles = Array.from(files).filter((f) =>
      f.type.startsWith("image/"),
    );
    if (imageFiles.length === 0) return;
    imageFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setAttachedImages((prev) => [
          ...prev,
          {
            id: Date.now() + Math.random(),
            file,
            dataUrl: e.target.result,
            name: file.name || "screenshot.png",
          },
        ]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (id) => {
    setAttachedImages((prev) => prev.filter((img) => img.id !== id));
  };

  // Ctrl+V 클립보드 붙여넣기
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageItems = Array.from(items).filter((item) =>
      item.type.startsWith("image/"),
    );
    if (imageItems.length === 0) return;
    e.preventDefault();
    const files = imageItems.map((item) => item.getAsFile()).filter(Boolean);
    addImages(files);
  };

  // 드래그앤드롭
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer?.files?.length > 0) addImages(e.dataTransfer.files);
  };

  // 파일 선택
  const handleFileSelect = (e) => {
    if (e.target.files?.length > 0) addImages(e.target.files);
    e.target.value = ""; // reset for re-select
  };

  // AI 요청 전송
  const handleSubmit = async (e) => {
    e?.preventDefault();
    const hasImages = attachedImages.length > 0;
    if ((!input.trim() && !hasImages) || isLoading) return;

    // 🔧 선택된 열에서 데이터 추출
    let columnsToUse = selectedColumns;
    let dataToUse = [];

    // AUIGrid에서 선택된 열의 데이터 추출
    if (window.AUIGrid && window.AUIGrid.isCreated("#horizontal_grid")) {
      try {
        const gridData = window.AUIGrid.getGridData("#horizontal_grid");
        console.log("📊 [AI] Grid data:", gridData?.length, "rows");
        console.log("📊 [AI] Selected columns:", columnsToUse);

        if (gridData?.length > 0 && columnsToUse.length > 0) {
          dataToUse = gridData.map((row, idx) => {
            const rowData = {};
            columnsToUse.forEach((col) => {
              rowData[col] = row[col] || "";
            });
            rowData._rowIndex = idx;
            return rowData;
          });
          console.log(
            `✅ [AI] Extracted ${dataToUse.length} rows from ${columnsToUse.length} columns`,
          );
        }
      } catch (err) {
        console.error("❌ [AI] Grid data extraction failed:", err);
      }
    }

    // 데이터/이미지가 없으면 → 일반 문의(Q&A) 채팅으로 폴백 (어떤 화면에서든 자유 질문)
    if (
      (!dataToUse || dataToUse.length === 0 || columnsToUse.length === 0) &&
      !hasImages
    ) {
      if (!input.trim()) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              "⚠️ 질문을 입력하거나, 열 선택 또는 이미지를 첨부해주세요.",
            isError: true,
          },
        ]);
        return;
      }
      const chatInput = input;
      setMessages((prev) => [...prev, { role: "user", content: chatInput }]);
      setInput("");
      setIsLoading(true);
      try {
        const selectedModel = normalizeGeminiGenerationModel(
          localStorage.getItem("aiAssistantModel"),
          GEMINI_FLASH_MODEL,
        );
        const resp = await fetch(`${apiBaseUrl}/api/ai/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: chatInput,
            history: messages
              .filter((m) => m.role === "user" || m.role === "assistant")
              .slice(-10)
              .map((m) => ({ role: m.role, content: m.content })),
            model: selectedModel,
          }),
        });
        // 비-JSON(HTML 404 등) 응답 가드 — /api/ai/chat 미등록 시 SPA index.html 폴백 방지
        const ct = resp.headers.get("content-type") || "";
        if (!resp.ok || !ct.includes("application/json")) {
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content:
                resp.status === 404
                  ? "⚠️ AI 채팅 엔드포인트(/api/ai/chat)를 찾을 수 없습니다. 백엔드(node server.js)를 재시작했는지 확인해주세요."
                  : `⚠️ 서버 오류 (HTTP ${resp.status}). 백엔드 상태를 확인해주세요.`,
              isError: true,
            },
          ]);
          return;
        }
        const r = await resp.json();
        if (r.success && r.reply) {
          setMessages((prev) => [
            ...prev,
            { role: "assistant", content: r.reply },
          ]);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: `⚠️ ${r.error || "응답을 받지 못했습니다."}`,
              isError: true,
            },
          ]);
        }
      } catch (e) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `❌ 연결 오류: ${e.message}`,
            isError: true,
          },
        ]);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // 🖼️ 이미지 dataUrl 수집 (전송용)
    const imageDataUrls = attachedImages.map((img) => img.dataUrl);

    const userMessage = {
      role: "user",
      content: hasImages
        ? `${input || "(이미지 첨부)"}${columnsToUse.length > 0 ? ` [열: ${columnsToUse.join(", ")}]` : ""}`
        : `[열: ${columnsToUse.join(", ")}] ${input}`,
      images: attachedImages.map((img) => img.dataUrl), // 메시지에 이미지 미리보기 저장
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setAttachedImages([]); // 전송 후 초기화
    setIsLoading(true);
    setPendingTransform(null);

    try {
      const selectedModel = normalizeGeminiGenerationModel(
        localStorage.getItem("aiAssistantModel"),
        GEMINI_FLASH_MODEL,
      );
      console.log(
        `[AI Assistant] Using model: ${selectedModel}, images: ${imageDataUrls.length}`,
      );
      const response = await fetch(`${apiBaseUrl}/api/ai/transform-data`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: input,
          columns: columnsToUse,
          data: dataToUse,
          model: selectedModel,
          images: imageDataUrls.length > 0 ? imageDataUrls : undefined,
          context: {
            totalRows: dataToUse?.length || 0,
            columnCount: columnsToUse.length,
            hasImages: imageDataUrls.length > 0,
          },
        }),
      });

      const result = await response.json();

      if (result.success) {
        const aiMessage = {
          role: "assistant",
          content: result.explanation || "변환 완료!",
          transformedData: result.transformedData,
          changes: result.changes || [],
          columns: columnsToUse, // 적용 시 사용할 컬럼 목록 저장
          originalData: dataToUse, // 🔧 미리보기용 원본 데이터 저장
        };
        setMessages((prev) => [...prev, aiMessage]);
        setPendingTransform({
          data: result.transformedData,
          columns: columnsToUse,
          originalData: dataToUse,
        });
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `⚠️ 오류: ${result.error || "처리 중 문제가 발생했습니다."}`,
            isError: true,
            details: result.details,
          },
        ]);
      }
    } catch (error) {
      console.error("AI 요청 실패:", error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `❌ 연결 오류: ${error.message}`,
          isError: true,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // 변환 적용 - AUIGrid에 직접 업데이트
  const handleApply = () => {
    if (!pendingTransform) return;

    const {
      data: transformedData,
      columns: pendingColumns,
      originalData,
    } = pendingTransform;
    const targetColumn = pendingColumns && pendingColumns[0];

    console.log(
      "🔧 [AI] handleApply - transformedData:",
      transformedData?.length,
    );

    if (
      window.AUIGrid &&
      document.getElementById("horizontal_grid") &&
      transformedData
    ) {
      try {
        const gridData = window.AUIGrid.getGridData("#horizontal_grid");

        // 1. 객체 기반 변환인지 확인
        const isObjectResult =
          transformedData.length > 0 && typeof transformedData[0] === "object";

        // 2. 행 필터링(삭제) 감지
        const hasFilteredRows =
          transformedData.length < originalData.length &&
          transformedData.every((item) => item._rowIndex !== undefined);

        if (hasFilteredRows) {
          console.log(
            "🗑️ [AI] 행 필터링 감지. 기존:",
            originalData.length,
            "-> 결과:",
            transformedData.length,
          );

          // 🔄 Undo 저장 (전체 그리드 데이터)
          const undoEntry = {
            id: Date.now(),
            type: "filter",
            beforeData: [...gridData],
            timestamp: new Date().toISOString(),
            prompt: input || "(필터링)",
          };
          setUndoStack((prev) => [...prev, undoEntry]);

          // 체크된 행(또는 필터링된 행)만 남기기
          const keptIndices = new Set(
            transformedData.map((item) => item._rowIndex),
          );

          // 삭제할 인덱스 추출 (역순)
          const indicesToRemove = originalData
            .map((d) => d._rowIndex)
            .filter((idx) => !keptIndices.has(idx))
            .sort((a, b) => b - a);

          console.log("🗑️ [AI] 삭제할 행 인덱스:", indicesToRemove);

          indicesToRemove.forEach((idx) => {
            window.AUIGrid.removeRow("#horizontal_grid", idx);
          });

          setMessages((prev) => [
            ...prev,
            {
              role: "system",
              content: `✅ 조건에 맞지 않는 **${indicesToRemove.length}개** 행이 삭제되었습니다.`,
            },
          ]);
        }

        // 3. 데이터 업데이트
        if (isObjectResult) {
          // 열 분리 또는 다중 필드 업데이트
          const firstItem = transformedData[0];
          const aiKeys = Object.keys(firstItem).filter(
            (k) => k !== "_rowIndex",
          );

          // 만약 새로운 열이 있는 "분리" 작업이라면 기존 로직 수행
          const existingColumns = window.AUIGrid.getColumnLayout(
            "#horizontal_grid",
          ).map((c) => c.dataField);
          const missingColumns = aiKeys.filter(
            (k) => !existingColumns.includes(k),
          );

          if (missingColumns.length > 0) {
            // 기존 Split 로직 호출 (생략 - 기존 팝업 로직 활용)
            // ... (기존 split 로직으로 연결)
          } else {
            // 기존 열에 데이터 업데이트
            transformedData.forEach((item) => {
              if (item._rowIndex !== undefined) {
                const updateObj = { ...item };
                delete updateObj._rowIndex;
                window.AUIGrid.updateRow(
                  "#horizontal_grid",
                  updateObj,
                  item._rowIndex,
                );
              }
            });

            setMessages((prev) => [
              ...prev,
              {
                role: "system",
                content: `✅ 데이터가 업데이트되었습니다.`,
              },
            ]);
          }
        } else if (targetColumn) {
          // 단순 문자열 배열 업데이트
          transformedData.forEach((newValue, index) => {
            const rowIndex = originalData?.[index]?._rowIndex ?? index;
            window.AUIGrid.updateRow(
              "#horizontal_grid",
              { [targetColumn]: newValue },
              rowIndex,
            );
          });
        }

        window.AUIGrid.resize("#horizontal_grid");
      } catch (err) {
        console.error("❌ [AI] 적용 실패:", err);
      }
    }
    setPendingTransform(null);
  };

  // 🔄 Undo: 마지막 변경 되돌리기
  const handleUndo = () => {
    if (undoStack.length === 0) {
      console.warn("🔄 [Undo] 되돌릴 변경이 없습니다.");
      return;
    }

    const lastChange = undoStack[undoStack.length - 1];
    console.log(
      "🔄 [Undo] 되돌리기:",
      lastChange.column || lastChange.sourceColumn,
      lastChange.beforeValues?.length + "개",
    );

    if (window.AUIGrid && window.AUIGrid.isCreated("#horizontal_grid")) {
      try {
        if (lastChange.type === "split") {
          // 🔀 열 분리 복원: 원본 열 값 복원 + 새로 생성된 열 삭제

          // 1. 원본 열 값 복원
          lastChange.beforeValues.forEach(({ rowIndex, value }) => {
            window.AUIGrid.updateRow(
              "#horizontal_grid",
              { [lastChange.sourceColumn]: value },
              rowIndex,
            );
          });

          // 2. 새로 생성된 열 삭제 (첫 번째는 원본 열이므로 제외)
          // 🔧 역순으로 삭제해야 인덱스가 밀리지 않음
          const columnsToRemove = (
            lastChange.newColumnNames?.slice(1) || []
          ).reverse();
          console.log("🔄 [Undo] 삭제할 열 (역순):", columnsToRemove);

          columnsToRemove.forEach((colName) => {
            try {
              // 열 인덱스 찾기
              const colIdx = window.AUIGrid.getColumnIndexByDataField(
                "#horizontal_grid",
                colName,
              );
              console.log("🔄 [Undo] 열 인덱스:", colName, "->", colIdx);

              if (colIdx !== -1 && colIdx !== undefined && colIdx !== null) {
                // AUIGrid removeColumn(columnIndex) 사용
                window.AUIGrid.removeColumn("#horizontal_grid", colIdx);
                console.log(
                  "✅ [Undo] 열 삭제 완료:",
                  colName,
                  "(인덱스:",
                  colIdx,
                  ")",
                );
              } else {
                console.warn("⚠️ [Undo] 열 인덱스를 찾을 수 없음:", colName);
              }
            } catch (removeErr) {
              console.warn("⚠️ [Undo] 열 삭제 실패:", colName, removeErr);
            }
          });

          // 3. 그리드 리사이즈
          setTimeout(() => {
            window.AUIGrid.resize("#horizontal_grid");
          }, 100);
        } else {
          // 일반 복원
          lastChange.beforeValues.forEach(({ rowIndex, value }) => {
            window.AUIGrid.updateRow(
              "#horizontal_grid",
              { [lastChange.column]: value },
              rowIndex,
            );
          });
        }

        setUndoStack((prev) => prev.slice(0, -1));

        // 🆕 열 목록 갱신
        refreshAvailableColumns();

        setMessages((prev) => [
          ...prev,
          {
            role: "system",
            content:
              lastChange.type === "split"
                ? `↩️ 열 분리 되돌림: **${lastChange.sourceColumn}** 복원, ${lastChange.newColumnNames?.slice(1).join(", ")} 열 삭제 완료!`
                : `↩️ **${lastChange.column || lastChange.sourceColumn}** 복원 완료!`,
          },
        ]);

        console.log("✅ [Undo] 복원 완료");
      } catch (err) {
        console.error("❌ [Undo] 복원 실패:", err);
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `❌ 되돌리기 실패: ${err.message}`,
            isError: true,
          },
        ]);
      }
    }
  };

  // 🔀 열 분리 데이터 적용 (내부 함수)
  const applySplitData = (
    sourceColumn,
    aiColumnKeys,
    newColumnNames,
    transformedData,
    originalData,
    gridData,
  ) => {
    console.log(
      "🔀 [Split] 데이터 적용 시작:",
      sourceColumn,
      "->",
      newColumnNames,
    );

    // Undo 저장
    const beforeValues = [];
    transformedData.forEach((_, index) => {
      if (index < gridData.length) {
        const rowIndex = originalData?.[index]?.rowIndex ?? index;
        beforeValues.push({
          rowIndex,
          value: gridData[rowIndex]?.[sourceColumn],
        });
      }
    });

    const undoEntry = {
      id: Date.now(),
      type: "split",
      sourceColumn,
      newColumnNames,
      beforeValues,
      afterValues: transformedData,
      timestamp: new Date().toISOString(),
      prompt: input || "(알 수 없음)",
    };
    setUndoStack((prev) => [...prev, undoEntry]);

    let updatedCount = 0;
    transformedData.forEach((aiValueObj, index) => {
      if (index < gridData.length) {
        const rowIndex = originalData?.[index]?.rowIndex ?? index;

        // AI 키 → 규칙 기반 열 이름으로 매핑
        const updateObj = {};
        aiColumnKeys.forEach((aiKey, i) => {
          updateObj[newColumnNames[i]] = aiValueObj[aiKey];
        });

        window.AUIGrid.updateRow("#horizontal_grid", updateObj, rowIndex);
        updatedCount++;
      }
    });

    console.log(
      `✅ [Split] 완료: ${updatedCount}행, 열: ${newColumnNames.join(", ")}`,
    );

    setMessages((prev) => [
      ...prev,
      {
        role: "system",
        content: `✅ **${sourceColumn}** → **${newColumnNames.join(", ")}** 열로 분리 완료! (${updatedCount}행)`,
      },
    ]);

    setPendingSplit(null);
    setPendingTransform(null);
  };

  // 🔀 열 분리 확인 버튼 클릭 핸들러
  const handleSplitConfirm = () => {
    if (!pendingSplit) return;

    const {
      sourceColumn,
      aiColumnKeys,
      newColumnNames,
      missingColumns,
      transformedData,
      originalData,
      gridData,
    } = pendingSplit;

    console.log("🔀 [Split] 사용자 확인 - 새 열 추가:", missingColumns);

    try {
      // 새 열 추가 (AUIGrid)
      const currentLayout = window.AUIGrid.getColumnLayout("#horizontal_grid");
      const sourceIndex = currentLayout.findIndex(
        (c) => c.dataField === sourceColumn,
      );

      missingColumns.forEach((colName, i) => {
        const newColumn = {
          dataField: colName,
          headerText: colName,
          width: 150,
          editable: true,
        };
        // 원본 열 바로 뒤에 삽입
        window.AUIGrid.addColumn(
          "#horizontal_grid",
          newColumn,
          sourceIndex + 1 + i,
        );
      });

      console.log("✅ [Split] 새 열 추가 완료:", missingColumns);

      // 데이터 적용
      applySplitData(
        sourceColumn,
        aiColumnKeys,
        newColumnNames,
        transformedData,
        originalData,
        gridData,
      );

      // ⭐ 컬럼 목록 즉시 갱신 - 새 컬럼이 선택박스에 나타나도록
      refreshAvailableColumns();
    } catch (err) {
      console.error("❌ [Split] 열 추가 실패:", err);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `❌ 열 추가 실패: ${err.message}`,
          isError: true,
        },
      ]);
    }
  };

  // ⭐ 컬럼 목록 갱신 헬퍼 함수
  const refreshAvailableColumns = () => {
    if (window.AUIGrid && window.AUIGrid.isCreated("#horizontal_grid")) {
      try {
        const columnLayout = window.AUIGrid.getColumnLayout("#horizontal_grid");
        const cols = columnLayout
          .filter(
            (c) =>
              c.dataField &&
              c.dataField !== "__checkbox" &&
              c.dataField !== "__rownum",
          )
          .map((c) => ({
            field: c.dataField,
            label: c.headerText || c.dataField,
          }));
        const prevFields = availableColumns.map((c) => c.field);
        const newFields = cols.map((c) => c.field);

        // 새로 추가된 컬럼 확인
        const addedFields = newFields.filter((f) => !prevFields.includes(f));

        setAvailableColumns(cols);

        // 새 컬럼도 선택 상태에 추가
        if (addedFields.length > 0) {
          setSelectedColumns((prev) => [...prev, ...addedFields]);
          console.log("📊 [AI] 새 컬럼 추가됨:", addedFields);
        }

        console.log("📊 [AI] 컬럼 목록 갱신:", cols.length, "개");
      } catch (err) {
        console.error("컬럼 정보 갱신 실패:", err);
      }
    }
  };

  // 빠른 명령 버튼 (설정에서 관리하는 키워드 기반)
  const quickCommands = React.useMemo(() => {
    try {
      const saved = localStorage.getItem("aiQuickCommands");
      if (saved) {
        const categories = JSON.parse(saved);
        return categories.flatMap((cat) =>
          cat.items.map((item) => ({ label: item, prompt: item })),
        );
      }
    } catch {}
    return [
      { label: "빈 셀 채우기", prompt: "빈 셀에 0을 채워줘" },
      { label: "값만 추출", prompt: "불필요한 텍스트 제거하고 값만 추출해줘" },
      { label: "형식 통일", prompt: "데이터 형식을 통일해줘" },
    ];
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className="h-full bg-white border-l border-gray-200 flex flex-row shadow-2xl flex-shrink-0 relative"
      style={{ width: panelWidth }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* 🖼️ 드래그 오버레이 */}
      {isDragging && (
        <div className="absolute inset-0 bg-blue-50/90 border-2 border-dashed border-blue-400 rounded-lg z-50 flex items-center justify-center">
          <div className="text-center">
            <ImageIcon className="w-10 h-10 text-blue-400 mx-auto mb-2" />
            <p className="text-sm font-medium text-blue-600">
              이미지를 여기에 놓으세요
            </p>
          </div>
        </div>
      )}
      {/* 리사이즈 핸들 (왼쪽 가장자리) */}
      <div
        className="absolute left-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-gray-200 transition-colors flex items-center justify-center group z-10"
        onMouseDown={(e) => {
          e.preventDefault();
          setIsResizing(true);
        }}
      >
        <div className="w-1 h-16 bg-gray-300 group-hover:bg-gray-500 rounded-full" />
      </div>

      <div className="flex-1 flex flex-col ml-2 min-w-0">
        {/* 헤더 */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-gray-700" />
            <span className="font-semibold text-gray-900">AI 어시스턴트</span>
            <span className="text-xs text-gray-400">({panelWidth}px)</span>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* 🔧 열 다중 선택 UI */}
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span className="text-gray-400">↪</span>
              <span className="font-medium">작업할 열 선택</span>
              <span className="text-xs text-gray-400">
                ({selectedColumns.length}/{availableColumns.length}개)
              </span>
            </div>
            <button
              onClick={() => setShowColumnSelector(!showColumnSelector)}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              {showColumnSelector ? "접기" : "열 선택"}
            </button>
          </div>

          {/* 선택된 열 요약 */}
          <div
            className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200 cursor-pointer hover:bg-gray-50"
            onClick={() => setShowColumnSelector(!showColumnSelector)}
          >
            <span className="text-gray-600">⊞</span>
            <span className="text-sm flex-1 truncate font-medium text-gray-800">
              {selectedColumns.length === availableColumns.length
                ? "전체 데이터"
                : selectedColumns.length === 0
                  ? "열을 선택하세요"
                  : selectedColumns.slice(0, 3).join(", ") +
                    (selectedColumns.length > 3
                      ? ` 외 ${selectedColumns.length - 3}개`
                      : "")}
            </span>
            <span
              className={`text-xs px-1.5 py-0.5 rounded ${selectedColumns.length === availableColumns.length ? "bg-gray-100 text-gray-600" : "bg-blue-50 text-blue-600"}`}
            >
              {selectedColumns.length === availableColumns.length
                ? "전체"
                : `${selectedColumns.length}개 선택`}
            </span>
          </div>

          {/* 열 선택 체크박스 목록 */}
          {showColumnSelector && availableColumns.length > 0 && (
            <div className="mt-2 p-2 bg-white rounded border border-gray-200 max-h-40 overflow-y-auto">
              {/* 전체 선택/해제 */}
              <div className="flex items-center gap-2 pb-2 mb-2 border-b border-gray-100">
                <input
                  type="checkbox"
                  id="select-all"
                  checked={selectedColumns.length === availableColumns.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedColumns(availableColumns.map((c) => c.field));
                    } else {
                      setSelectedColumns([]);
                    }
                  }}
                  className="w-3 h-3 rounded border-gray-300"
                />
                <label
                  htmlFor="select-all"
                  className="text-xs font-medium text-gray-700 cursor-pointer"
                >
                  전체 선택
                </label>
              </div>

              {/* 개별 컬럼 체크박스 */}
              <div className="grid grid-cols-2 gap-1">
                {availableColumns.map((col) => (
                  <label
                    key={col.field}
                    className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer hover:text-gray-900 py-0.5"
                  >
                    <input
                      type="checkbox"
                      checked={selectedColumns.includes(col.field)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedColumns([...selectedColumns, col.field]);
                        } else {
                          setSelectedColumns(
                            selectedColumns.filter((f) => f !== col.field),
                          );
                        }
                      }}
                      className="w-3 h-3 rounded border-gray-300"
                    />
                    <span className="truncate" title={col.label}>
                      {col.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* 빠른 작업 안내 */}
          {selectedColumns.length > 0 && (
            <div className="mt-2 text-xs text-gray-500">
              💡{" "}
              {selectedColumns.length === 1
                ? "1개 열 선택됨 - 분리/추출 가능"
                : `${selectedColumns.length}개 열 선택됨 - 합치기 가능`}
            </div>
          )}
        </div>

        {/* 🔧 빠른 액션 버튼 (Google Sheets 스타일) */}
        <div className="px-3 py-2 border-b border-gray-100 bg-white">
          <div className="text-xs text-gray-400 mb-1.5">빠른 변환</div>
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() =>
                setInput("Incoterms 코드만 추출해줘 (예: FCA, FOB, CIF 등)")
              }
              className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition border border-gray-200"
            >
              코드만 추출
            </button>
            <button
              onClick={() => setInput("빈 셀을 이전 값으로 채워줘")}
              className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition border border-gray-200"
            >
              빈 셀 채우기
            </button>
            <button
              onClick={() => setInput("선택된 열들을 하나로 합쳐줘")}
              className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition border border-gray-200"
            >
              열 합치기
            </button>
            <button
              onClick={() => setInput("하나의 열을 여러 열로 분리해줘")}
              className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition border border-gray-200"
            >
              열 분리
            </button>
            <button
              onClick={() => setInput("데이터 형식을 통일해줘")}
              className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition border border-gray-200"
            >
              형식 통일
            </button>
          </div>

          {/* 🔄 되돌리기 버튼 - 깔끔한 회색 스타일 */}
          {undoStack.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <button
                onClick={handleUndo}
                className="text-xs px-3 py-1.5 bg-gray-50 text-gray-700 rounded hover:bg-gray-100 transition border border-gray-200 flex items-center gap-1.5 shadow-sm"
              >
                <RotateCcw className="w-3.5 h-3.5 text-gray-500" />
                <span className="font-medium">되돌리기</span>
                <span className="text-gray-400 text-[10px]">
                  ({undoStack.length})
                </span>
              </button>
            </div>
          )}
        </div>

        {/* 메시지 영역 */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 ? (
            <div className="text-center text-gray-400 py-8">
              <Sparkles className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">컬럼을 선택하고</p>
              <p className="text-sm">변환할 내용을 입력하세요</p>
              <p className="text-xs text-gray-300 mt-4">
                ← 왼쪽 가장자리를 드래그하여 크기 조절
              </p>
            </div>
          ) : (
            messages.map((msg, idx) => (
              <div
                key={idx}
                className={`
                                ${msg.role === "user" ? "ml-8" : "mr-4"}
                                ${msg.role === "system" ? "text-center text-xs text-gray-500" : ""}
                            `}
              >
                {msg.role === "system" ? (
                  <div className="py-1">
                    <span>{msg.content}</span>
                    {msg.preview && (
                      <div className="text-xs text-gray-400 mt-1 truncate">
                        {msg.preview}
                      </div>
                    )}
                    {/* 🔀 열 분리 확인 버튼 */}
                    {msg.isSplitConfirm && pendingSplit && (
                      <div className="mt-3 flex justify-center">
                        <button
                          onClick={handleSplitConfirm}
                          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition shadow-sm flex items-center gap-2"
                        >
                          <span>➕</span>
                          <span>열 추가하고 분리하기</span>
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    className={`
                                        rounded-lg px-3 py-2 text-sm
                                        ${
                                          msg.role === "user"
                                            ? "bg-gray-100 text-gray-900"
                                            : msg.isError
                                              ? "bg-red-50 text-red-700 border border-red-200"
                                              : "bg-gray-100 text-gray-800"
                                        }
                                    `}
                  >
                    <div className="whitespace-pre-wrap font-medium">
                      {msg.content}
                    </div>

                    {/* �️ 첨부 이미지 표시 */}
                    {msg.images && msg.images.length > 0 && (
                      <div className="mt-2 flex gap-1.5 flex-wrap">
                        {msg.images.map((src, imgIdx) => (
                          <img
                            key={imgIdx}
                            src={src}
                            alt={`첨부 ${imgIdx + 1}`}
                            className="w-20 h-20 object-cover rounded border border-gray-200 cursor-pointer hover:opacity-80 transition"
                            onClick={() => window.open(src, "_blank")}
                          />
                        ))}
                      </div>
                    )}

                    {/* �🔧 에러 상세 정보 (Debug용) */}
                    {msg.isError && msg.details && (
                      <div className="mt-2 p-2 bg-red-100/50 text-red-900 text-[10px] font-mono overflow-auto max-h-40 whitespace-pre border border-red-200 rounded">
                        {msg.details}
                      </div>
                    )}

                    {/* 변환 결과 미리보기 */}
                    {msg.transformedData && (
                      <div className="mt-2 p-2 bg-white rounded border border-gray-200">
                        <div className="text-xs text-gray-500 mb-1">
                          변환 결과 미리보기:
                        </div>
                        <div className="text-xs space-y-1 max-h-40 overflow-y-auto">
                          {msg.transformedData.slice(0, 8).map((item, i) => (
                            <div
                              key={i}
                              className="flex items-center gap-2 flex-wrap"
                            >
                              <span className="text-gray-400 line-through truncate max-w-[120px]">
                                {(() => {
                                  // 메시지에 저장된 당시의 원본 데이터를 참조
                                  const rowIndex =
                                    item._rowIndex !== undefined
                                      ? item._rowIndex
                                      : i;
                                  const originalRow =
                                    msg.originalData?.[rowIndex] ||
                                    msg.originalData?.[i];
                                  const firstCol =
                                    (msg.columns && msg.columns[0]) ||
                                    selectedColumns[0];
                                  const displayVal =
                                    originalRow?.[firstCol] || "(데이터 없음)";
                                  return typeof displayVal === "object"
                                    ? JSON.stringify(displayVal)
                                    : displayVal;
                                })()}
                              </span>
                              <span className="text-gray-400">→</span>
                              <span className="text-blue-600 font-medium">
                                {typeof item === "object"
                                  ? item[selectedColumns[0]] ||
                                    Object.values(item).filter(
                                      (v) => v !== item._rowIndex,
                                    )[0] ||
                                    JSON.stringify(item)
                                  : item}
                              </span>
                            </div>
                          ))}
                          {msg.transformedData.length > 8 && (
                            <div className="text-gray-400">
                              ...외 {msg.transformedData.length - 8}개
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}

          {/* 로딩 표시 */}
          {isLoading && (
            <div className="flex items-center gap-2 text-gray-500">
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span className="text-sm">AI가 분석 중...</span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* 적용/취소 버튼 */}
        {pendingTransform && (
          <div className="px-3 py-1.5 border-t border-gray-200 bg-gray-50">
            <div className="flex gap-2">
              <button
                onClick={() => setPendingTransform(null)}
                className="flex-1 py-1.5 bg-white hover:bg-gray-100 text-gray-600 border border-gray-300 rounded text-xs flex items-center justify-center gap-1.5 transition-colors"
              >
                <X className="w-3 h-3" />
                취소
              </button>
              <button
                onClick={handleApply}
                className="flex-1 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs flex items-center justify-center gap-1.5 transition-colors shadow-sm"
              >
                <Check className="w-3 h-3" />
                적용
              </button>
            </div>
          </div>
        )}

        {/* 빠른 명령 */}
        <div className="px-3 py-1.5 border-t border-gray-100">
          <div className="flex gap-1.5 flex-wrap">
            {quickCommands.map((cmd, idx) => (
              <button
                key={idx}
                onClick={() => setInput(cmd.prompt)}
                className="px-2 py-0.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full transition-colors"
              >
                {cmd.label}
              </button>
            ))}
          </div>
        </div>

        {/* 입력 영역 */}
        <form onSubmit={handleSubmit} className="p-3 border-t border-gray-200">
          {/* 🖼️ 첨부 이미지 미리보기 */}
          {attachedImages.length > 0 && (
            <div className="flex gap-2 mb-2 flex-wrap">
              {attachedImages.map((img) => (
                <div key={img.id} className="relative group">
                  <img
                    src={img.dataUrl}
                    alt={img.name}
                    className="w-16 h-16 object-cover rounded-lg border border-gray-200 shadow-sm"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(img.id)}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
                  >
                    <X className="w-3 h-3" />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[8px] text-center rounded-b-lg truncate px-0.5">
                    {img.name}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            {/* 📎 파일 첨부 버튼 */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="px-2 py-2.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
              title="이미지 첨부 (Ctrl+V로 스크린샷 붙여넣기 가능)"
            >
              <Paperclip className="w-4 h-4" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onPaste={handlePaste}
              placeholder={
                attachedImages.length > 0
                  ? "이미지에 대해 질문하세요..."
                  : "예: FCA만 추출해줘 (Ctrl+V: 스크린샷)"
              }
              className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={
                (!input.trim() && attachedImages.length === 0) || isLoading
              }
              className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-lg transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AIChatPanel;
